import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import "./i18n";

// Main Pages
import Home from "./pages/Home";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import CSR from "./pages/CSR";
import FarmerCommunity from "./pages/FarmerCommunity";
import QualitySustainability from "./pages/QualitySustainability";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// About Pages
import WhoWeAre from "./pages/About/WhoWeAre";
import OurStory from "./pages/About/OurStory";
import RND from "./pages/About/RND";
import News from "./pages/About/News";
import NewsDetail from "./pages/About/NewsDetail";

// Product Pages
import CannedBottleFruit from "./pages/Products/CannedBottleFruit";
import DehydratedFruits from "./pages/Products/DehydratedFruits";
import FruitJuicePulp from "./pages/Products/FruitJuicePulp";
import FruitPowder from "./pages/Products/FruitPowder";
import JackfruitBananaBlossom from "./pages/Products/JackfruitBananaBlossom";
import KingCoconutWater from "./pages/Products/KingCoconutWater";
import Spices from "./pages/Products/Spices";
import CoconutProducts from "./pages/Products/CoconutProducts";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import Dashboard from "./pages/admin/Dashboard";
import ManageUsers from "./pages/admin/ManageUsers";
import AddNews from "./pages/admin/AddNews";
import AddBlog from "./pages/admin/AddBlog";
import ManageNews from "./pages/admin/ManageNews";
import ManageBlogs from "./pages/admin/ManageBlogs";
import EditNews from "./pages/admin/EditNews";
import EditBlog from "./pages/admin/EditBlog";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Super Admin Only Route Component
const SuperAdminRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");
  const userStr = localStorage.getItem("adminUser");
  const user = userStr ? JSON.parse(userStr) : null;

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  if (user?.role !== "super_admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

// Admin & Super Admin Route Component
const AdminRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");
  const userStr = localStorage.getItem("adminUser");
  const user = userStr ? JSON.parse(userStr) : null;

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  if (user?.role !== "admin" && user?.role !== "super_admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-white dark:bg-dark-900 transition-colors duration-300">
        <Routes>
          {/* Admin Routes (No Navbar/Footer) */}
          <Route path="/admin/login" element={<AdminLogin />} />

          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Add News/Blog - All authenticated users */}
          <Route
            path="/admin/news/create"
            element={
              <ProtectedRoute>
                <AddNews />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/blog/create"
            element={
              <ProtectedRoute>
                <AddBlog />
              </ProtectedRoute>
            }
          />

          {/* Manage News/Blog - Admin and Super Admin only */}
          <Route
            path="/admin/news"
            element={
              <AdminRoute>
                <ManageNews />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/blog"
            element={
              <AdminRoute>
                <ManageBlogs />
              </AdminRoute>
            }
          />

          {/* Edit News/Blog - Protected Routes (Admin and Super Admin) */}
          <Route
            path="/admin/news/edit/:id"
            element={
              <AdminRoute>
                <EditNews />
              </AdminRoute>
            }
          />

          <Route
            path="/admin/blog/edit/:id"
            element={
              <AdminRoute>
                <EditBlog />
              </AdminRoute>
            }
          />

          {/* Manage Users - Super Admin only */}
          <Route
            path="/admin/users"
            element={
              <SuperAdminRoute>
                <ManageUsers />
              </SuperAdminRoute>
            }
          />

          {/* Home */}
          <Route
            path="/"
            element={
              <>
                <Navbar />
                <Home />
                <Footer />
              </>
            }
          />

          {/* About Routes */}
          <Route
            path="/about/who-we-are"
            element={
              <>
                <Navbar />
                <WhoWeAre />
                <Footer />
              </>
            }
          />
          <Route
            path="/about/our-story"
            element={
              <>
                <Navbar />
                <OurStory />
                <Footer />
              </>
            }
          />
          <Route
            path="/about/rnd"
            element={
              <>
                <Navbar />
                <RND />
                <Footer />
              </>
            }
          />
          <Route
            path="/about/news"
            element={
              <>
                <Navbar />
                <News />
                <Footer />
              </>
            }
          />
          <Route
            path="/about/news/:slug"
            element={
              <>
                <Navbar />
                <NewsDetail />
                <Footer />
              </>
            }
          />

          {/* Product Routes */}
          <Route
            path="/products/canned-bottle-fruit"
            element={
              <>
                <Navbar />
                <CannedBottleFruit />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/dehydrated-fruits"
            element={
              <>
                <Navbar />
                <DehydratedFruits />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/fruit-juice-pulp"
            element={
              <>
                <Navbar />
                <FruitJuicePulp />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/fruit-powder"
            element={
              <>
                <Navbar />
                <FruitPowder />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/jackfruit-banana-blossom"
            element={
              <>
                <Navbar />
                <JackfruitBananaBlossom />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/king-coconut-water"
            element={
              <>
                <Navbar />
                <KingCoconutWater />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/spices"
            element={
              <>
                <Navbar />
                <Spices />
                <Footer />
              </>
            }
          />
          <Route
            path="/products/coconut-products"
            element={
              <>
                <Navbar />
                <CoconutProducts />
                <Footer />
              </>
            }
          />

          {/* Other Pages */}
          <Route
            path="/farmer-community"
            element={
              <>
                <Navbar />
                <FarmerCommunity />
                <Footer />
              </>
            }
          />
          <Route
            path="/quality-sustainability"
            element={
              <>
                <Navbar />
                <QualitySustainability />
                <Footer />
              </>
            }
          />
          <Route
            path="/insights"
            element={
              <>
                <Navbar />
                <Blog />
                <Footer />
              </>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <>
                <Navbar />
                <BlogDetail />
                <Footer />
              </>
            }
          />
          <Route
            path="/news"
            element={
              <>
                <Navbar />
                <News />
                <Footer />
              </>
            }
          />
          <Route
            path="/csr"
            element={
              <>
                <Navbar />
                <CSR />
                <Footer />
              </>
            }
          />
          <Route
            path="/contact"
            element={
              <>
                <Navbar />
                <Contact />
                <Footer />
              </>
            }
          />
          <Route
            path="/privacy"
            element={
              <>
                <Navbar />
                <PrivacyPolicy />
                <Footer />
              </>
            }
          />
          <Route
            path="/terms"
            element={
              <>
                <Navbar />
                <TermsOfService />
                <Footer />
              </>
            }
          />

          {/* 404 - Redirect to Home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ThemeProvider>
  );
}

export default App;
